// Staff equipment view - equipment management

import { css, html } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { router } from '../../lib/router.js';

// Pencil icon SVG
const pencilIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="16"
  height="16"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <path
    d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"
  />
</svg>`;

// Trash icon SVG
const trashIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="16"
  height="16"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <path d="M3 6h18" />
  <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
  <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
</svg>`;

// Clipboard icon SVG
const clipboardIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="14"
  height="14"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
</svg>`;

/**
 * Staff equipment view component for managing equipment
 */
class StaffEquipmentView extends BaseComponent {
  static properties = {
    _equipment: { type: Array, state: true },
    _loading: { type: Boolean, state: true },
    _error: { type: String, state: true },
    _categoryFilter: { type: String, state: true },
    _showDeleteModal: { type: Boolean, state: true },
    _equipmentToDelete: { type: String, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }
  `;

  constructor() {
    super();
    /** @type {Array<import('../../types/jsdoc-types.js').Equipment>} */
    this._equipment = [];
    this._loading = true;
    this._error = null;
    this._categoryFilter = null;
    this._showDeleteModal = false;
    this._equipmentToDelete = null;
  }

  async connectedCallback() {
    await super.connectedCallback();
    this._loadEquipment();
  }

  async _loadEquipment() {
    this._loading = true;
    this._error = null;

    try {
      this._equipment = await api.getEquipment();
    } catch (err) {
      console.error('Failed to load equipment:', err);
      this._error = /** @type {Error} */ (err).message || 'Failed to load equipment';
    } finally {
      this._loading = false;
    }
  }

  get _filteredEquipment() {
    if (this._categoryFilter === null) {
      return this._equipment;
    }
    return this._equipment.filter(
      (/** @type {import('../../types/jsdoc-types.js').Equipment} */ item) => item.category === this._categoryFilter,
    );
  }

  /** @param {string | null} category */
  _setFilter(category) {
    this._categoryFilter = category;
  }

  _goToNewEquipment() {
    router.navigate('/staff/equipment/new');
  }

  /** @param {string} slug */
  _editEquipment(slug) {
    router.navigate(`/staff/equipment/${slug}/edit`);
  }

  /** @param {string} slug */
  _deleteEquipment(slug) {
    this._equipmentToDelete = slug;
    this._showDeleteModal = true;
  }

  async _confirmDelete() {
    if (!this._equipmentToDelete) return;

    try {
      await api.delete(`/api/equipment/${this._equipmentToDelete}`);
      this._equipmentToDelete = null;
      this._showDeleteModal = false;
      await this._loadEquipment();
    } catch (err) {
      console.error('Failed to delete equipment:', err);
      this._error = /** @type {Error} */ (err).message || 'Failed to delete equipment';
    }
  }

  _cancelDelete() {
    this._showDeleteModal = false;
    this._equipmentToDelete = null;
  }

  /** @param {string} category */
  _formatCategory(category) {
    if (category === 'adventuring-gear') return 'Gear';
    return category.charAt(0).toUpperCase() + category.slice(1);
  }

  /** @param {string} category */
  _getCategoryBadgeClass(category) {
    switch (category) {
      case 'weapon':
        return 'bg-danger';
      case 'armor':
        return 'bg-info';
      case 'adventuring-gear':
        return 'bg-secondary';
      default:
        return 'bg-secondary';
    }
  }

  /** @param {import('../../types/jsdoc-types.js').Equipment} item */
  _getWeaponProperties(item) {
    /** @type {Array<string>} */
    const props = [];
    const wp = item.weapon?.properties;
    if (!wp) return props;

    if (wp.finesse) props.push('finesse');
    if (wp.light) props.push('light');
    if (wp.heavy) props.push('heavy');
    if (wp.twoHanded) props.push('two-handed');
    if (wp.thrown) props.push('thrown');
    if (wp.ammunition) props.push('ammunition');
    if (wp.loading) props.push('loading');
    if (wp.reach) props.push('reach');
    return props;
  }

  /** @param {import('../../types/jsdoc-types.js').Equipment} item */
  _formatArmorClass(item) {
    const armor = item.armor;
    if (!armor?.ac) return 'N/A';

    if (armor.category === 'shield') {
      return `+${armor.ac.add || 0}`;
    }

    let ac = `${armor.ac.base || 0}`;
    if (armor.ac.addDexModifier) {
      if (armor.ac.maxDexModifier !== undefined && armor.ac.maxDexModifier !== null) {
        ac += ` + DEX (max ${armor.ac.maxDexModifier})`;
      } else {
        ac += ' + DEX';
      }
    }
    return ac;
  }

  /**
   * @param {string} text
   * @param {Event} event
   */
  _copyToClipboard(text, event) {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        const btn = /** @type {HTMLElement} */ (event.target).closest('button');
        if (btn) {
          btn.classList.add('btn-success');
          btn.classList.remove('btn-outline-secondary');
          setTimeout(() => {
            btn.classList.remove('btn-success');
            btn.classList.add('btn-outline-secondary');
          }, 2000);
        }
      })
      .catch((err) => {
        console.error('Failed to copy:', err);
      });
  }

  render() {
    return html`
      <nav aria-label="breadcrumb">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">Staff</li>
          <li class="breadcrumb-item active" aria-current="page">Equipment</li>
        </ol>
      </nav>

      ${this._error ? html` <div class="alert alert-danger">${this._error}</div> ` : ''}

      <div class="d-flex justify-content-between align-items-center mb-3">
        <h2>Equipment</h2>
        <button class="btn btn-primary" @click="${this._goToNewEquipment}">Create Equipment</button>
      </div>

      <div class="mb-3">
        <div class="btn-group" role="group" aria-label="Filter by category">
          <button
            type="button"
            class="btn ${this._categoryFilter === null ? 'btn-primary' : 'btn-outline-primary'}"
            @click="${() => this._setFilter(null)}"
          >
            All
          </button>
          <button
            type="button"
            class="btn ${this._categoryFilter === 'weapon' ? 'btn-primary' : 'btn-outline-primary'}"
            @click="${() => this._setFilter('weapon')}"
          >
            Weapons
          </button>
          <button
            type="button"
            class="btn ${this._categoryFilter === 'armor' ? 'btn-primary' : 'btn-outline-primary'}"
            @click="${() => this._setFilter('armor')}"
          >
            Armor
          </button>
          <button
            type="button"
            class="btn ${this._categoryFilter === 'adventuring-gear' ? 'btn-primary' : 'btn-outline-primary'}"
            @click="${() => this._setFilter('adventuring-gear')}"
          >
            Adventuring Gear
          </button>
        </div>
      </div>

      ${
        this._loading
          ? html`
            <div class="text-center py-4">
              <div class="spinner-border">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
          `
          : this._filteredEquipment.length === 0
            ? html` <p class="text-muted">No equipment found.</p> `
            : html`
              <div class="d-flex flex-wrap gap-3">
                ${this._filteredEquipment.map(
                  (item) => html`
                    <div style="flex: 1 1 280px; min-width: 260px; max-width: 320px;">
                      <div class="card h-100">
                        <div class="card-body">
                          <div class="d-flex justify-content-between align-items-start mb-2">
                            <h6 class="card-title mb-0">${item.name}</h6>
                            <div class="d-flex gap-1">
                              <button
                                class="btn btn-sm btn-outline-primary"
                                title="Edit"
                                @click="${() => this._editEquipment(item.slug)}"
                              >
                                ${pencilIcon}
                              </button>
                              <button
                                class="btn btn-sm btn-outline-danger"
                                title="Delete"
                                @click="${() => this._deleteEquipment(item.slug)}"
                              >
                                ${trashIcon}
                              </button>
                            </div>
                          </div>

                          <div class="d-flex align-items-center gap-2 mb-2">
                            <code class="small text-muted">${item.slug}</code>
                            <button
                              class="btn btn-sm btn-outline-secondary py-0 px-2"
                              title="Copy Slug"
                              @click="${(/** @type {Event} */ e) => this._copyToClipboard(item.slug, e)}"
                            >
                              ${clipboardIcon}
                            </button>
                          </div>

                          <div class="mb-2">
                            <div class="d-flex gap-1 mb-2">
                              <span class="badge ${this._getCategoryBadgeClass(item.category)}"
                                >${this._formatCategory(item.category)}</span
                              >
                              <span class="badge bg-secondary">v${item.version || 1}</span>
                              ${
                                item.published
                                  ? html` <span class="badge bg-success">Published</span> `
                                  : html` <span class="badge bg-warning text-dark">Draft</span> `
                              }
                            </div>
                          </div>

                          <div class="small">
                            ${
                              item.category === 'weapon' && item.weapon
                                ? html`
                                  <div>
                                    <strong>Damage:</strong> ${item.weapon.damage?.dice} ${item.weapon.damage?.type}
                                  </div>
                                  ${
                                    item.weapon.properties?.versatile
                                      ? html` <div><strong>Versatile:</strong> ${item.weapon.properties.versatile}</div> `
                                      : ''
                                  }
                                  ${
                                    item.weapon.properties?.range
                                      ? html`
                                        <div>
                                          <strong>Range:</strong> ${item.weapon.properties.range.normal}/${
                                            item.weapon.properties.range.long
                                          }
                                          ft
                                        </div>
                                      `
                                      : ''
                                  }
                                  ${
                                    this._getWeaponProperties(item).length > 0
                                      ? html`
                                        <div>
                                          <strong>Properties:</strong> ${this._getWeaponProperties(item).join(', ')}
                                        </div>
                                      `
                                      : ''
                                  }
                                `
                                : item.category === 'armor' && item.armor
                                  ? html`
                                    <div><strong>AC:</strong> ${this._formatArmorClass(item)}</div>
                                    ${
                                      item.armor.category
                                        ? html` <div><strong>Category:</strong> ${item.armor.category}</div> `
                                        : ''
                                    }
                                    ${
                                      item.armor.minStr
                                        ? html` <div><strong>Min STR:</strong> ${item.armor.minStr}</div> `
                                        : ''
                                    }
                                    ${
                                      item.armor.stealthDisadvantage
                                        ? html` <div><strong>Stealth:</strong> Disadvantage</div> `
                                        : ''
                                    }
                                  `
                                  : item.category === 'adventuring-gear'
                                    ? html`
                                      ${
                                        item.light
                                          ? html`
                                            <div>
                                              <strong>Light:</strong> ${item.light.brightRadius || 0}ft bright,
                                              ${item.light.dimRadius || 0}ft dim
                                            </div>
                                          `
                                          : ''
                                      }
                                      ${
                                        item.description
                                          ? html`
                                            <div class="text-muted fst-italic">
                                              ${item.description.substring(0, 60)}${
                                                item.description.length > 60 ? '...' : ''
                                              }
                                            </div>
                                          `
                                          : ''
                                      }
                                    `
                                    : ''
                            }

                            <div><strong>Cost:</strong> ${item.cost}</div>
                            ${item.weight ? html`<div><strong>Weight:</strong> ${item.weight} lb</div>` : ''}
                            ${
                              item.actions && item.actions.length > 0
                                ? html` <div><strong>Actions:</strong> ${item.actions.length}</div> `
                                : ''
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  `,
                )}
              </div>
            `
      }

      <!-- Delete Confirmation Modal -->
      ${
        this._showDeleteModal
          ? html`
            <div class="modal show d-block" style="background: rgba(0,0,0,0.5);">
              <div class="modal-dialog">
                <div class="modal-content">
                  <div class="modal-header">
                    <h5 class="modal-title">Delete Equipment</h5>
                    <button type="button" class="btn-close" @click="${this._cancelDelete}"></button>
                  </div>
                  <div class="modal-body">
                    <p>Are you sure you want to delete this equipment? This cannot be undone.</p>
                  </div>
                  <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" @click="${this._cancelDelete}">Cancel</button>
                    <button type="button" class="btn btn-danger" @click="${this._confirmDelete}">Delete</button>
                  </div>
                </div>
              </div>
            </div>
          `
          : ''
      }
    `;
  }
}

customElements.define('staff-equipment-view', StaffEquipmentView);
